import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { clientApi, useClientAuth } from "@/context/ClientAuth";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

const inputCls = "w-full bg-[#F9F6F1] border border-[#E5E7EB] focus:border-[#041C3E] outline-none px-4 py-3 text-sm transition-colors";

export default function Login() {
  const { user, loading, setUser } = useClientAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin"); // signin | signup
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [signin, setSignin] = useState({ email: "", password: "" });
  const [signup, setSignup] = useState({ name: "", email: "", password: "", phone: "", location: "", postal_code: "", terms_accepted: false });
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard");
  }, [user, loading, navigate]);

  const signInWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submitSignin = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const res = await clientApi.post("/client/login", signin);
      localStorage.setItem("client_token", res.data.session_token);
      setUser(res.data.user);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Could not sign in. Please try again.");
    } finally { setBusy(false); }
  };

  const submitSignup = async (e) => {
    e.preventDefault();
    setError("");
    if (!signup.terms_accepted) { setError("Please accept the Terms and Conditions to continue."); return; }
    setBusy(true);
    try {
      const res = await clientApi.post("/client/register", signup);
      localStorage.setItem("client_token", res.data.session_token);
      setUser(res.data.user);
      toast.success("Welcome! Your account is ready.");
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Could not create your account. Please try again.");
    } finally { setBusy(false); }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await clientApi.post("/client/forgot-password", { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not send the reset link. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#041C3E] text-white flex flex-col" data-testid="client-login-page">
      <header className="px-6 h-16 flex items-center justify-between max-w-6xl mx-auto w-full">
        <Link to="/" className="font-head font-black text-lg">The Next Move<span className="text-[#D94833]"> by Rhellex</span></Link>
        <Link to="/" className="text-sm text-white/60 hover:text-white transition-colors">Back to home</Link>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md bg-white text-[#041C3E] p-8 sm:p-10"
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D94833]">Client access</p>
          <h1 className="font-head text-3xl font-black mt-3 leading-tight">
            {mode === "signin" ? "Sign in to your client space" : mode === "signup" ? "Create your client account" : "Reset your password"}
          </h1>

          {mode !== "forgot" && (
            <>
              <button
                data-testid="google-signin-button"
                onClick={signInWithGoogle}
                className="mt-6 w-full flex items-center justify-center gap-3 border border-[#041C3E] px-6 py-3.5 font-semibold tracking-wide hover:bg-[#041C3E] hover:text-white transition-colors"
              >
                <GoogleIcon /> Continue with Google
              </button>

              <div className="flex items-center gap-3 my-6">
                <div className="h-px bg-[#E5E7EB] flex-1" />
                <span className="text-xs text-[#9CA3AF] uppercase tracking-[0.15em]">or with email</span>
                <div className="h-px bg-[#E5E7EB] flex-1" />
              </div>
            </>
          )}

          {error && <p data-testid="client-auth-error" className="mb-4 text-sm text-[#D94833]">{error}</p>}

          {mode === "forgot" && (
            forgotSent ? (
              <div data-testid="forgot-sent" className="mt-6">
                <p className="text-sm text-[#3A4A63] leading-relaxed">
                  If an account exists for that email, we have sent a link to reset your password. Please check your inbox (and spam). The link is valid for one hour.
                </p>
                <button type="button" data-testid="forgot-back-to-signin" onClick={() => { setMode("signin"); setForgotSent(false); setError(""); }}
                  className="mt-6 font-semibold text-[#D94833] underline underline-offset-2 text-sm">Back to sign in</button>
              </div>
            ) : (
              <form onSubmit={submitForgot} className="mt-6 space-y-3" data-testid="forgot-form">
                <p className="text-sm text-[#3A4A63]">Enter your email and we will send you a link to reset your password.</p>
                <input data-testid="forgot-email" type="email" required placeholder="Email address" className={inputCls}
                  value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} />
                <button data-testid="forgot-submit" type="submit" disabled={busy}
                  className="w-full bg-[#041C3E] text-white px-8 py-4 font-semibold tracking-wide hover:bg-[#D94833] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                  {busy && <Loader2 size={16} className="animate-spin" />} {busy ? "Sending…" : "Send reset link"}
                </button>
                <p className="text-sm text-[#3A4A63] pt-2">
                  <button type="button" data-testid="forgot-back" onClick={() => { setMode("signin"); setError(""); }}
                    className="font-semibold text-[#D94833] underline underline-offset-2">Back to sign in</button>
                </p>
              </form>
            )
          )}

          {mode === "signin" ? (
            <form onSubmit={submitSignin} className="space-y-3" data-testid="signin-form">
              <input data-testid="signin-email" type="email" required placeholder="Email address" className={inputCls}
                value={signin.email} onChange={(e) => setSignin({ ...signin, email: e.target.value })} />
              <input data-testid="signin-password" type="password" required placeholder="Password" className={inputCls}
                value={signin.password} onChange={(e) => setSignin({ ...signin, password: e.target.value })} />
              <div className="text-right">
                <button type="button" data-testid="forgot-link" onClick={() => { setMode("forgot"); setError(""); setForgotSent(false); }}
                  className="text-xs font-semibold text-[#3A4A63] hover:text-[#D94833] underline underline-offset-2">Forgot password?</button>
              </div>
              <button data-testid="signin-submit" type="submit" disabled={busy}
                className="w-full bg-[#041C3E] text-white px-8 py-4 font-semibold tracking-wide hover:bg-[#D94833] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {busy && <Loader2 size={16} className="animate-spin" />} {busy ? "Signing in…" : "Sign in"}
              </button>
              <p className="text-sm text-[#3A4A63] pt-2">
                New here?{" "}
                <button type="button" data-testid="switch-to-signup" onClick={() => { setMode("signup"); setError(""); }}
                  className="font-semibold text-[#D94833] underline underline-offset-2">Create an account</button>
              </p>
            </form>
          ) : mode === "signup" ? (
            <form onSubmit={submitSignup} className="space-y-3" data-testid="signup-form">
              <input data-testid="signup-name" required minLength={2} placeholder="Full name" className={inputCls}
                value={signup.name} onChange={(e) => setSignup({ ...signup, name: e.target.value })} />
              <input data-testid="signup-email" type="email" required placeholder="Email address" className={inputCls}
                value={signup.email} onChange={(e) => setSignup({ ...signup, email: e.target.value })} />
              <input data-testid="signup-phone" required minLength={5} placeholder="Phone number" className={inputCls}
                value={signup.phone} onChange={(e) => setSignup({ ...signup, phone: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="signup-location" required minLength={2} placeholder="Where are you based?" className={inputCls}
                  value={signup.location} onChange={(e) => setSignup({ ...signup, location: e.target.value })} />
                <input data-testid="signup-postal" required minLength={2} placeholder="Zip / postal code" className={inputCls}
                  value={signup.postal_code} onChange={(e) => setSignup({ ...signup, postal_code: e.target.value })} />
              </div>
              <input data-testid="signup-password" type="password" required minLength={8} placeholder="Password (min 8 characters)" className={inputCls}
                value={signup.password} onChange={(e) => setSignup({ ...signup, password: e.target.value })} />
              <label className="flex items-start gap-2 text-sm text-[#3A4A63] pt-1 cursor-pointer">
                <input data-testid="signup-terms" type="checkbox" className="mt-1"
                  checked={signup.terms_accepted} onChange={(e) => setSignup({ ...signup, terms_accepted: e.target.checked })} />
                <span>I accept the <Link to="/terms" target="_blank" className="font-semibold text-[#041C3E] underline underline-offset-2">Terms and Conditions</Link>.</span>
              </label>
              <button data-testid="signup-submit" type="submit" disabled={busy}
                className="w-full bg-[#041C3E] text-white px-8 py-4 font-semibold tracking-wide hover:bg-[#D94833] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {busy && <Loader2 size={16} className="animate-spin" />} {busy ? "Creating account…" : "Create account"}
              </button>
              <p className="text-sm text-[#3A4A63] pt-2">
                Already have an account?{" "}
                <button type="button" data-testid="switch-to-signin" onClick={() => { setMode("signin"); setError(""); }}
                  className="font-semibold text-[#D94833] underline underline-offset-2">Sign in</button>
              </p>
            </form>
          ) : null}
        </motion.div>
      </div>
    </div>
  );
}
